import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import * as alertifyjs from 'alertifyjs';
import {
  AppClientsService,
  AppClientRow,
  AppClientsResponse,
} from 'src/app/services/app-clients/app-clients.service';

/**
 * Vista "Clientes con App" (Parámetros del Sistema).
 *
 * Lista los clientes que usan la app nueva (Flutter) con la versión que tienen
 * instalada. Fuente 100% core (`GET /api/admin/app-clients`); no consume el BFF.
 * El detalle por cliente se abre en un drawer del lado derecho (NgbOffcanvas).
 */
@Component({
  selector: 'app-app-clients',
  templateUrl: './app-clients.component.html',
  styleUrls: ['./app-clients.component.css'],
})
export class AppClientsComponent implements OnInit {
  @ViewChild('detailDrawer') detailDrawer!: TemplateRef<any>;

  rows: AppClientRow[] = [];
  loading = false;
  total = 0;
  page = 1;
  limit = 50;

  search = '';
  channel: 'all' | 'new' = 'all';
  sort: 'recent' | 'name' = 'recent';

  stats = { total_with_app: 0, with_version: 0, uses_new_app: 0 };

  selected: AppClientRow | null = null;
  exporting = false;

  private search$ = new Subject<string>();
  // Secuencia de carga: descarta respuestas que llegan fuera de orden (una
  // búsqueda con debounce + un cambio de filtro pueden resolver en desorden).
  private loadSeq = 0;

  constructor(
    private appClientsService: AppClientsService,
    private offcanvasService: NgbOffcanvas,
  ) {}

  ngOnInit(): void {
    // Debounce del buscador: no dispara una request por tecla.
    this.search$
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.page = 1;
        this.load();
      });
    this.load();
  }

  load = async (): Promise<void> => {
    const seq = ++this.loadSeq;
    this.loading = true;
    try {
      const resp: AppClientsResponse | undefined = await this.appClientsService.getAppClients({
        page: this.page,
        limit: this.limit,
        search: this.search,
        channel: this.channel,
        sort: this.sort,
      });
      // Si mientras tanto se disparó otra carga, esta respuesta quedó obsoleta.
      if (seq !== this.loadSeq) return;
      if (resp) {
        this.rows = resp.rows || [];
        this.total = resp.total || 0;
        this.stats = resp.stats || this.stats;
      } else {
        this.rows = [];
        this.total = 0;
      }
    } finally {
      // Solo la última carga controla el spinner.
      if (seq === this.loadSeq) this.loading = false;
    }
  };

  onSearchChange(value: string): void {
    this.search = value;
    this.search$.next(value);
  }

  onChannelChange(value: 'all' | 'new'): void {
    this.channel = value;
    this.page = 1;
    this.load();
  }

  onSortChange(value: 'recent' | 'name'): void {
    this.sort = value;
    this.page = 1;
    this.load();
  }

  // --- Paginación ---
  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.limit));
  }

  get rangeFrom(): number {
    return this.total === 0 ? 0 : (this.page - 1) * this.limit + 1;
  }

  get rangeTo(): number {
    return Math.min(this.page * this.limit, this.total);
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages || p === this.page) return;
    this.page = p;
    this.load();
  }

  prevPage(): void {
    this.goToPage(this.page - 1);
  }

  nextPage(): void {
    this.goToPage(this.page + 1);
  }

  // --- Drawer de detalle (lado derecho) ---
  openDetail(row: AppClientRow): void {
    this.selected = row;
    this.offcanvasService.open(this.detailDrawer, {
      position: 'end',
      panelClass: 'offcanvas-end-wide',
    });
  }

  closeDrawer(): void {
    this.offcanvasService.dismiss();
  }

  // --- Exportar a Excel ---
  // Descarga TODAS las filas que matchean el filtro actual (búsqueda + canal),
  // no solo la página visible: pagina el endpoint hasta juntarlas todas. El
  // Excel incluye los campos de la tabla + los del detalle (correo, código de
  // persona, sesión, creado).
  exportExcel = async (): Promise<void> => {
    if (this.exporting) return;
    this.exporting = true;
    try {
      const all: AppClientRow[] = [];
      const pageSize = 200;
      let p = 1;
      // Cota de seguridad por si el total no cierra (evita bucle infinito).
      for (let guard = 0; guard < 100; guard++) {
        const resp: AppClientsResponse | undefined = await this.appClientsService.getAppClients({
          page: p,
          limit: pageSize,
          search: this.search,
          channel: this.channel,
          sort: this.sort,
        });
        const batch = resp?.rows || [];
        if (!batch.length) break;
        all.push(...batch);
        if (all.length >= (resp?.total || 0) || batch.length < pageSize) break;
        p++;
      }

      if (!all.length) {
        alertifyjs.warning('No hay clientes para exportar con el filtro actual.');
        return;
      }

      const fmt = (d: string | null) => (d ? new Date(d).toLocaleString('es-VE') : '');
      const data = all.map((r) => ({
        'Cliente': this.fullName(r),
        'Cédula': r.username || '',
        'Correo': r.email || '',
        'Versión de la app': r.app_version ? `v${r.app_version}` : 'Sin reportar',
        'Últ. inicio de sesión': r.uses_new_app ? 'App V2' : 'App V1',
        'Versión detectada el': fmt(r.app_version_updated_at),
        'Estado': r.status ? 'Activo' : 'Inactivo',
        'Sesión': r.is_online ? 'En línea' : 'Sin sesión',
        'Código de persona': r.person_code || '',
        'Creado': fmt(r.created_at),
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      ws['!cols'] = [
        { wch: 26 }, { wch: 12 }, { wch: 28 }, { wch: 14 }, { wch: 18 },
        { wch: 20 }, { wch: 10 }, { wch: 14 }, { wch: 38 }, { wch: 20 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Clientes con App');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const stamp = new Date().toISOString().slice(0, 10);
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `clientes_con_app_${stamp}.xlsx`);
    } catch (error) {
      console.error('Error exportando Excel:', error);
      alertifyjs.error('No se pudo generar el Excel.');
    } finally {
      this.exporting = false;
    }
  };

  // --- Helpers de presentación ---
  fullName(row: AppClientRow): string {
    return `${row.name || ''} ${row.lastName || ''}`.trim() || '—';
  }

  initials(row: AppClientRow): string {
    const a = (row.name || '').trim().charAt(0);
    const b = (row.lastName || '').trim().charAt(0);
    return (a + b).toUpperCase() || (row.username || '?').charAt(0).toUpperCase();
  }

  trackById(_index: number, row: AppClientRow): string {
    return row.id;
  }
}
